import { ReactElement } from "react";
import { Link } from "react-router-dom";
import { localize } from "src/l10n";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import useAttention from "../../useAttention";
import AttentionRow from "../AttentionRow/AttentionRow";
import "./AttentionBlock.scss";

interface IProps {
    /**
     * How many rows to show before pointing at the full list.
     */
    maxRows?: number;
}

const defaultMaxRows = 3;

function AttentionBlock(props: IProps): ReactElement | null {
    const { list, isLoading, hasFailed, reload } = useAttention();
    const maxRows = props.maxRows ?? defaultMaxRows;

    if (!list || !list.applicable) {
        // Not for this person. An empty list would say "you are done", which is a
        // different thing and not true of someone the surface was never for.
        return null;
    }

    if (isLoading) {
        return (
            <div className="AttentionBlock">
                <div className="AttentionBlock-header">
                    <span className="AttentionBlock-title">{localize("ATTENTION_TITLE")}</span>
                </div>
                {Array.from({ length: maxRows }).map((_, index) => (
                    <div className="AttentionBlock-skeleton" key={index} aria-hidden="true" />
                ))}
            </div>
        );
    }

    const rows = list.rows.slice(0, maxRows);
    const isIncomplete = hasFailed || list.unavailableSources.length > 0;
    const hasOverdue = list.rows.some((row) => row.isOverdue);

    return (
        <div className="AttentionBlock">
            <div className="AttentionBlock-header">
                <span className="AttentionBlock-heading">
                    <span className="AttentionBlock-title">{localize("ATTENTION_TITLE")}</span>
                    {list.count > 0 && (
                        <span
                            className={hasOverdue
                                ? "AttentionBlock-counter AttentionBlock-counter--late"
                                : "AttentionBlock-counter"}
                        >
                            {list.count}
                        </span>
                    )}
                </span>

                {list.count > 0 && (
                    <Link className="AttentionBlock-showAll" to="/just-nu">
                        {`${localize("ATTENTION_SHOW_ALL")} ${list.count}`}
                        <Visage2Icon icon="arrow-right-3" size="extra-small" />
                    </Link>
                )}
            </div>

            {isIncomplete && (
                <div className="AttentionBlock-incomplete">
                    {/* Never "Allt klart" when something could not be read: a short
                        list and a broken one look identical otherwise. */}
                    <span className="AttentionBlock-incompleteText">
                        {localize("ATTENTION_INCOMPLETE")}
                    </span>
                    <button className="AttentionBlock-retry" type="button" onClick={reload}>
                        {localize("ATTENTION_RETRY")}
                    </button>
                </div>
            )}

            {list.count === 0 && !isIncomplete && (
                <div className="AttentionBlock-allClear">
                    <span className="AttentionBlock-allClearIcon" aria-hidden="true">
                        <Visage2Icon icon="tick-circle" size="extra-small" />
                    </span>
                    <span className="AttentionBlock-allClearText">
                        {localize("ATTENTION_ALL_CLEAR")}
                    </span>
                </div>
            )}

            {rows.map((row, index) => (
                <AttentionRow
                    key={`${row.sourceType}-${row.sourceItemId}`}
                    row={row}
                    isPrimary={index === 0}
                />
            ))}
        </div>
    );
}

export default AttentionBlock;
